/**
 * Labels for how an answer was made, as Claude shows them: a "Thought for 6s" line, one line per
 * tool step ("Searching the web for “…”"), a summary for a group of steps, and specific errors.
 * Pure, so the components stay thin and this is unit-tested.
 */

// ---------------------------------------------------------------------------------------------
// Thinking

const EFFORT_LABELS: Record<string, string> = {
  minimal: "Minimal effort",
  low: "Low effort",
  medium: "Medium effort",
  high: "High effort",
};

/** "High effort", or undefined for an effort the picker doesn't know. */
export function effortLabel(effort: unknown): string | undefined {
  return typeof effort === "string" && Object.hasOwn(EFFORT_LABELS, effort)
    ? EFFORT_LABELS[effort]
    : undefined;
}

function formatSeconds(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

/** The thinking line: "Thinking" while it streams, then "Thought for 6s". */
export function thinkingLabel({ running, ms }: { running: boolean; ms: number | undefined }) {
  if (running) return "Thinking";
  if (ms === undefined) return "Thought process";
  if (ms < 1000) return "Thought for a moment";
  return `Thought for ${formatSeconds(ms)}`;
}

/**
 * How long a group of reasoning parts took, from the `thinking.ms` the server stores in the
 * message's metadata (one entry per reasoning part, in order). `ordinal` is how many reasoning
 * parts come before the group. Undefined when any of them wasn't timed (older messages).
 */
export function thinkingDuration(
  thinking: unknown,
  ordinal: number,
  count: number,
): number | undefined {
  const ms = (thinking as { ms?: unknown } | null | undefined)?.ms;
  if (!Array.isArray(ms) || count <= 0) return undefined;
  let total = 0;
  for (let i = ordinal; i < ordinal + count; i++) {
    const value = ms[i];
    if (typeof value !== "number" || value <= 0) return undefined;
    total += value;
  }
  return total;
}

// ---------------------------------------------------------------------------------------------
// Tool steps

export type ToolStep = {
  toolName: string;
  args: Record<string, unknown> | undefined;
  /** assistant-ui's tool-call status type. */
  status: "running" | "complete" | "incomplete" | "requires-action";
  result?: unknown;
  /** True while the model is still writing the arguments. */
  argsStreaming?: boolean;
};

function stringArg(args: ToolStep["args"], key: string): string | undefined {
  const value = args?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function host(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, "") || undefined;
  } catch {
    return undefined;
  }
}

/** "get_weather" → "get weather". */
function humanize(toolName: string): string {
  return (
    toolName
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim()
      .toLowerCase() || "a tool"
  );
}

/** Whether the step failed: an error status, or a tool that returned `{ error }`. */
export function stepFailed(step: Pick<ToolStep, "status" | "result">): boolean {
  if (step.status === "incomplete") return true;
  const result = step.result;
  return typeof result === "object" && result !== null && "error" in result;
}

/** One line for a tool call: "Searching the web for “kourai”", "Read example.com". */
export function describeToolStep(step: ToolStep): string {
  const running = step.status === "running" || step.status === "requires-action";
  const more = step.argsStreaming ? "…" : "";

  switch (step.toolName) {
    case "web_search": {
      const query = stringArg(step.args, "query");
      if (!query) return running ? "Searching the web" : "Searched the web";
      return `${running ? "Searching" : "Searched"} the web for “${query}${more}”`;
    }
    case "fetch_page": {
      const url = stringArg(step.args, "url");
      const target =
        (!step.argsStreaming && url && host(url)) || (running ? "a page" : url) || "a page";
      return `${running ? "Reading" : "Read"} ${target}`;
    }
    default: {
      const browser = describeBrowserStep(step, running);
      return browser ?? `${running ? "Using" : "Used"} ${humanize(step.toolName)}`;
    }
  }
}

/** “Continue button”, from the description the model gives each element it acts on. */
function elementArg(step: ToolStep): string {
  const element = stringArg(step.args, "element");
  return element ? `“${element}”` : "an element";
}

/** Lines for the browser agent's tools (ADMIN-217), or undefined for any other tool. */
function describeBrowserStep(step: ToolStep, running: boolean): string | undefined {
  const pick = (now: string, done: string) => (running ? now : done);
  switch (step.toolName) {
    case "snapshot":
      return pick("Looking at the page", "Looked at the page");
    case "click":
      return `${pick("Clicking", "Clicked")} ${elementArg(step)}`;
    case "type": {
      const text = stringArg(step.args, "text");
      const what = text && text.length <= 40 ? ` “${text}”` : "";
      return `${pick("Typing", "Typed")}${what} into ${elementArg(step)}`;
    }
    case "select": {
      const values = step.args?.values;
      const first = Array.isArray(values) && typeof values[0] === "string" ? ` “${values[0]}”` : "";
      return `${pick("Choosing", "Chose")}${first} in ${elementArg(step)}`;
    }
    case "scroll":
      return pick("Scrolling", "Scrolled");
    case "extract":
      return pick("Reading the page", "Read the page");
    case "wait": {
      const text = stringArg(step.args, "text");
      return text ? `${pick("Waiting for", "Waited for")} “${text}”` : pick("Waiting", "Waited");
    }
    case "navigate":
    case "open_tab": {
      const url = stringArg(step.args, "url");
      const target = (url && host(url.includes("://") ? url : `https://${url}`)) || "a page";
      return step.toolName === "navigate"
        ? `${pick("Going to", "Went to")} ${target}`
        : `${pick("Opening", "Opened")} ${target} in a new tab`;
    }
    case "list_tabs":
      return pick("Checking open tabs", "Checked open tabs");
    case "close_tab":
      return pick("Closing a tab", "Closed a tab");
    case "screenshot":
      return pick("Taking a screenshot", "Took a screenshot");
    default:
      return undefined;
  }
}

const BROWSER_STEPS = new Set([
  "snapshot",
  "click",
  "type",
  "select",
  "scroll",
  "extract",
  "wait",
  "navigate",
  "open_tab",
  "list_tabs",
  "close_tab",
  "screenshot",
]);

/** A short note after the step's label: "6 results", "no results". */
export function stepDetail(step: ToolStep): string | undefined {
  if (step.status === "running") return undefined;
  const result = step.result as { results?: unknown } | undefined;
  if (step.toolName === "web_search" && Array.isArray(result?.results)) {
    const n = result.results.length;
    return n === 0 ? "no results" : `${n} result${n === 1 ? "" : "s"}`;
  }
  return undefined;
}

/**
 * The line for a group of steps. While one runs it's that step ("Reading example.com"); after,
 * a summary: "Searched the web twice, read 3 pages", "Used 2 tools", plus "1 failed".
 */
export function summarizeToolSteps(steps: readonly ToolStep[]): string {
  const running = steps.findLast((s) => s.status === "running" || s.status === "requires-action");
  if (running) return describeToolStep(running);

  let searches = 0;
  let reads = 0;
  let browserSteps = 0;
  let others = 0;
  let failed = 0;
  for (const step of steps) {
    if (stepFailed(step)) failed++;
    if (step.toolName === "web_search") searches++;
    else if (step.toolName === "fetch_page") reads++;
    else if (BROWSER_STEPS.has(step.toolName)) browserSteps++;
    else others++;
  }

  const phrases: string[] = [];
  if (searches === 1) phrases.push("searched the web");
  else if (searches === 2) phrases.push("searched the web twice");
  else if (searches > 2) phrases.push(`searched the web ${searches} times`);
  if (reads) phrases.push(reads === 1 ? "read a page" : `read ${reads} pages`);
  if (browserSteps) {
    phrases.push(
      browserSteps === 1
        ? "took 1 step in the browser"
        : `took ${browserSteps} steps in the browser`,
    );
  }
  if (others) phrases.push(others === 1 ? "used a tool" : `used ${others} tools`);

  const text = phrases.join(", ") || "used tools";
  const summary = text.charAt(0).toUpperCase() + text.slice(1);
  return failed ? `${summary} · ${failed} failed` : summary;
}

// ---------------------------------------------------------------------------------------------
// Errors

export type ChatErrorView = {
  code: string;
  title: string;
  detail: string;
  /** Whether "Try again" is likely to help. */
  retry: boolean;
};

type ErrorCopy = { title: string; hint: string; retry: boolean };

const UNKNOWN_ERROR: ErrorCopy = {
  title: "Something went wrong",
  hint: "Try again in a moment.",
  retry: true,
};

const ERROR_VIEWS: Record<string, ErrorCopy> = {
  rate_limited: {
    title: "Too many requests",
    hint: "Wait a few seconds, then try again.",
    retry: true,
  },
  provider_down: {
    title: "The model's provider is down",
    hint: "Try again, or pick another model.",
    retry: true,
  },
  aborted: {
    title: "The answer was cut off",
    hint: "The connection dropped before it finished.",
    retry: true,
  },
  context_too_long: {
    title: "This chat is too long",
    hint: "Start a new chat, or pick a model with a longer context.",
    retry: false,
  },
  unavailable: {
    title: "This model isn't available",
    hint: "Pick another model and try again.",
    retry: true,
  },
  unknown: UNKNOWN_ERROR,
};

/**
 * Reads the error a failed answer carries. The ChatAgent sends `[code] message`
 * (`turn-metadata.ts`); anything else (a dropped socket, an older server) counts as unknown and
 * keeps its own text.
 */
export function parseChatError(error: unknown): ChatErrorView {
  const raw =
    typeof error === "string"
      ? error
      : typeof (error as { message?: unknown } | null)?.message === "string"
        ? (error as { message: string }).message
        : "";
  const match = /^\[([a-z_]+)\]\s*([\s\S]*)$/.exec(raw.trim());
  const code = match?.[1] && Object.hasOwn(ERROR_VIEWS, match[1]) ? match[1] : "unknown";
  const view = ERROR_VIEWS[code] ?? UNKNOWN_ERROR;
  const message = (match ? match[2] : raw)?.trim();
  const detail =
    code === "unknown" && message && message !== "An error occurred."
      ? message
      : `${message && match ? `${message} ` : ""}${view.hint}`;
  return { code, title: view.title, detail, retry: view.retry };
}
